import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Image, CheckCircle, AlertCircle, Loader2, Sparkles } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface BackfillResult {
  campaignId: string;
  campaignName: string;
  status: 'success' | 'error' | 'skipped';
  message: string;
  creativesProcessed: number;
}

interface CreativeMetadata {
  url: string;
  locationName?: string;
  mediaOwner?: string;
  orientation?: 'portrait' | 'landscape';
  scheduledDays?: number[];
  resolution?: string;
}

// Extract media owner from URL
const extractMediaOwnerFromUrl = (url: string): string | null => {
  const lowerUrl = url.toLowerCase();
  
  // Known media owners with specific patterns
  if (lowerUrl.includes('alan-landscape') || lowerUrl.includes('alan_landscape')) return 'Alan-landscape';
  if (lowerUrl.includes('alan-portrait') || lowerUrl.includes('alan_portrait')) return 'Alan-portrait';
  if (lowerUrl.includes('alan')) return 'Alan';
  if (lowerUrl.includes('tableads')) return 'TableAds';
  if (lowerUrl.includes('pixelads')) return 'PixelAds';
  if (lowerUrl.includes('stc')) return 'STC';
  
  // Try to extract from path pattern: /media-owner-name/filename or timestamp-LocationName-file
  try {
    const decodedUrl = decodeURIComponent(url);
    
    // Pattern: timestamp-LocationName-filename
    const timestampPattern = /\d{13}-([^-/]+)-/;
    const timestampMatch = decodedUrl.match(timestampPattern);
    if (timestampMatch?.[1]) {
      return timestampMatch[1];
    }
    
    // Pattern: folder/MediaOwner/filename
    const pathPattern = /\/([A-Za-z][A-Za-z0-9_-]+)\//g;
    const matches = [...decodedUrl.matchAll(pathPattern)];
    if (matches.length > 0) {
      const lastMatch = matches[matches.length - 1][1];
      if (!['creatives', 'media', 'uploads', 'public', 'campaign_creatives'].includes(lastMatch.toLowerCase())) {
        return lastMatch;
      }
    }
  } catch {
    // Ignore decode errors
  }
  
  return null;
};

// Extract orientation from URL
const extractOrientationFromUrl = (url: string): 'landscape' | 'portrait' | undefined => {
  try {
    const decodedUrl = decodeURIComponent(url).toLowerCase();
    if (decodedUrl.includes('landscape') || decodedUrl.includes('-l-') || decodedUrl.includes('_l_')) {
      return 'landscape';
    }
    if (decodedUrl.includes('portrait') || decodedUrl.includes('-p-') || decodedUrl.includes('_p_')) {
      return 'portrait';
    }
  } catch {
    // Ignore decode errors
  }
  return undefined;
};

// Extract resolution from URL
const extractResolutionFromUrl = (url: string): string | undefined => {
  const resolutionPattern = /(\d{3,4})x(\d{3,4})/i;
  const match = url.match(resolutionPattern);
  if (match) {
    return `${match[1]}×${match[2]}`;
  }
  return undefined;
};

// Extract scheduled day from URL filename pattern (e.g. "-day3-" or "_day3_")
const extractScheduledDayFromUrl = (url: string): number | undefined => {
  try {
    const decoded = decodeURIComponent(url);
    const match = decoded.match(/(?:^|[-_])day(\d{1,3})(?:[-_]|\.|$)/i);
    const day = match?.[1] ? Number(match[1]) : NaN;
    return Number.isFinite(day) && day > 0 ? day : undefined;
  } catch {
    return undefined;
  }
};

// Parse creative metadata from URLs and locations
const parseCreativeMetadata = (
  creativeUrls: string[],
  selectedLocations: any
): CreativeMetadata[] => {
  if (!creativeUrls || creativeUrls.length === 0) return [];
  
  // Build location map for orientation lookup
  const locationOrientationMap = new Map<string, 'portrait' | 'landscape'>();
  
  if (selectedLocations) {
    const locations = Array.isArray(selectedLocations) ? selectedLocations : [];
    for (const loc of locations) {
      if (loc?.name && loc?.screenType) {
        const screenType = String(loc.screenType).toLowerCase();
        const orientation = screenType.includes('portrait') ? 'portrait' : 
                           screenType.includes('landscape') ? 'landscape' : undefined;
        if (orientation) {
          locationOrientationMap.set(loc.name, orientation);
        }
      }
      // Also map media owner if available
      if (loc?.mediaOwner && loc?.screenType) {
        const screenType = String(loc.screenType).toLowerCase();
        const orientation = screenType.includes('portrait') ? 'portrait' : 
                           screenType.includes('landscape') ? 'landscape' : undefined;
        if (orientation) {
          locationOrientationMap.set(loc.mediaOwner, orientation);
        }
      }
    }
  }
  
  return creativeUrls.map(url => {
    const mediaOwner = extractMediaOwnerFromUrl(url);
    let orientation = extractOrientationFromUrl(url);
    const resolution = extractResolutionFromUrl(url);

    // Try to get orientation from location map if not found in URL
    if (!orientation && mediaOwner) {
      orientation = locationOrientationMap.get(mediaOwner);
    }

    // Infer orientation from resolution if still not found
    if (!orientation && resolution) {
      const [width, height] = resolution.split('×').map(Number);
      if (width && height) {
        orientation = width > height ? 'landscape' : 'portrait';
      }
    }

    const scheduledDay = extractScheduledDayFromUrl(url);

    const metadata: CreativeMetadata = {
      url,
      mediaOwner: mediaOwner || undefined,
      orientation,
      resolution,
      scheduledDays: scheduledDay ? [scheduledDay] : [],
    };

    // Set location name if we can match it
    if (mediaOwner) {
      metadata.locationName = mediaOwner;
    }

    return metadata;
  });
};

export const CreativeMetadataBackfillTool = () => {
  const [isScanning, setIsScanning] = useState(false);
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [results, setResults] = useState<BackfillResult[]>([]);
  const [campaignsNeedingBackfill, setCampaignsNeedingBackfill] = useState<any[]>([]);
  const { toast } = useToast();

  const scanForMissingMetadata = async () => {
    setIsScanning(true);
    setResults([]);
    
    try {
      // Find campaigns with creatives but missing/empty metadata
      const { data: savedCampaigns, error: savedError } = await supabase
        .from('saved_campaigns')
        .select('id, campaign_id, campaign_name, creative_urls, creative_metadata, selected_locations, created_at')
        .order('created_at', { ascending: false });

      if (savedError) throw savedError;

      // Filter campaigns that have creatives but no metadata
      const needsBackfill = savedCampaigns?.filter(sc => {
        const hasCreatives = sc.creative_urls && sc.creative_urls.length > 0;
        const metadataEmpty = !sc.creative_metadata || 
          (Array.isArray(sc.creative_metadata) && sc.creative_metadata.length === 0);
        return hasCreatives && metadataEmpty;
      }) || [];

      setCampaignsNeedingBackfill(needsBackfill);

      toast({
        title: "Scan Complete",
        description: `Found ${needsBackfill.length} campaigns with missing creative metadata`,
      });
    } catch (error) {
      console.error('Error scanning campaigns:', error);
      toast({
        title: "Scan Failed",
        description: "Could not scan campaigns for missing metadata",
        variant: "destructive",
      });
    } finally {
      setIsScanning(false);
    }
  };

  const runBackfill = async () => {
    if (campaignsNeedingBackfill.length === 0) {
      toast({
        title: "No Campaigns to Backfill",
        description: "Please scan for missing metadata first",
        variant: "destructive",
      });
      return;
    }

    setIsBackfilling(true);
    const backfillResults: BackfillResult[] = [];

    try {
      for (const campaign of campaignsNeedingBackfill) {
        const result: BackfillResult = {
          campaignId: campaign.campaign_id,
          campaignName: campaign.campaign_name || 'Unknown',
          status: 'skipped',
          message: 'No creatives found',
          creativesProcessed: 0,
        };

        try {
          if (!campaign.creative_urls || campaign.creative_urls.length === 0) {
            result.status = 'skipped';
            result.message = 'No creative URLs to process';
          } else {
            // Parse metadata from URLs and locations
            const metadata = parseCreativeMetadata(
              campaign.creative_urls,
              campaign.selected_locations
            );
            
            // Update saved_campaigns with the new metadata
            const { error: updateError } = await supabase
              .from('saved_campaigns')
              .update({
                creative_metadata: JSON.parse(JSON.stringify(metadata)),
                updated_at: new Date().toISOString(),
              })
              .eq('id', campaign.id);

            if (updateError) {
              result.status = 'error';
              result.message = updateError.message;
            } else {
              result.status = 'success';
              result.creativesProcessed = metadata.length;
              result.message = `Parsed metadata for ${metadata.length} creatives`;
              
              // Also update the main campaigns table if it exists
              const { error: mainUpdateError } = await supabase
                .from('campaigns')
                .update({
                  creative_metadata: JSON.parse(JSON.stringify(metadata)),
                })
                .eq('id', campaign.campaign_id);
              
              if (mainUpdateError) {
                console.warn(`Could not update main campaigns table: ${mainUpdateError.message}`);
              }
            }
          }
        } catch (err: any) {
          result.status = 'error';
          result.message = err.message || 'Unknown error';
        }

        backfillResults.push(result);
      }

      setResults(backfillResults);

      const successCount = backfillResults.filter(r => r.status === 'success').length;
      const errorCount = backfillResults.filter(r => r.status === 'error').length;
      const totalCreatives = backfillResults.reduce((sum, r) => sum + r.creativesProcessed, 0);

      toast({
        title: "Backfill Complete",
        description: `${successCount} campaigns updated (${totalCreatives} creatives processed), ${errorCount} errors`,
        variant: successCount > 0 ? "default" : "destructive",
      });

      // Clear the scan list
      setCampaignsNeedingBackfill([]);

    } catch (error: any) {
      console.error('Error during backfill:', error);
      toast({
        title: "Backfill Failed",
        description: error.message || "An error occurred during backfill",
        variant: "destructive",
      });
    } finally {
      setIsBackfilling(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Image className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-100 text-green-800">Success</Badge>;
      case 'error':
        return <Badge variant="destructive">Error</Badge>;
      default:
        return <Badge variant="secondary">Skipped</Badge>;
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          Creative Metadata Backfill Tool
        </CardTitle>
        <CardDescription>
          Re-parse creative URLs and selected_locations to extract orientation, resolution, and other metadata
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-4">
          <Button 
            onClick={scanForMissingMetadata} 
            disabled={isScanning || isBackfilling}
            variant="outline"
          >
            {isScanning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <Image className="mr-2 h-4 w-4" />
                Scan for Missing Metadata
              </>
            )}
          </Button>

          <Button 
            onClick={runBackfill} 
            disabled={isBackfilling || isScanning || campaignsNeedingBackfill.length === 0}
          >
            {isBackfilling ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Backfill Metadata ({campaignsNeedingBackfill.length})
              </>
            )}
          </Button>
        </div>

        {campaignsNeedingBackfill.length > 0 && results.length === 0 && (
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">
              Found <strong>{campaignsNeedingBackfill.length}</strong> campaigns with creatives but no metadata.
              Click "Backfill Metadata" to parse and save metadata from creative URLs.
            </p>
          </div>
        )}

        {results.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium">Backfill Results</h4>
            <ScrollArea className="h-[300px] border rounded-lg p-4">
              <div className="space-y-3">
                {results.map((result, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg"
                  >
                    {getStatusIcon(result.status)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{result.campaignName}</span>
                        {getStatusBadge(result.status)}
                        {result.creativesProcessed > 0 && (
                          <Badge variant="outline" className="text-xs">
                            {result.creativesProcessed} creatives
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{result.message}</p>
                      <p className="text-xs text-muted-foreground mt-1 font-mono">
                        ID: {result.campaignId}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
